// apps/web/src/components/ProtectedRoute.tsx

import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuthStore } from "@/store/authStore";
import { Loader2 } from "lucide-react";

const ProtectedRoute = () => {
  const { user } = useAuthStore();
  const location = useLocation();

  // Show a loading spinner while the auth state is being determined
  if (user === undefined) {
    return (
        <div className="w-full h-screen flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-primary"/>
        </div>
    );
  }

  // 1. If the user is not logged in, redirect them to the login page.
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // 2. Define which routes are part of the onboarding flow.
  const isOnboardingRoute = location.pathname.startsWith('/onboarding');

  // Allow transporters to access vehicle registration during onboarding
  if (user.role === 'TRANSPORTER' && location.pathname === '/dashboard/my-vehicles') {
    return <Outlet />;
  }
  
  const verificationStatus = user.verificationStatus;
  const onboardingStatus = user.onboardingStatus;

  // 3. Handle users who have submitted verification (pending_admin_approval)
  if (onboardingStatus === 'pending_admin_approval') {
    // They should be on the pending page
    if (location.pathname !== '/onboarding/pending') {
      return <Navigate to="/onboarding/pending" replace />;
    }
  }

  // 4. Handle UNVERIFIED or REJECTED users who need to submit/resubmit documents
  if ((verificationStatus === 'UNVERIFIED' || verificationStatus === 'REJECTED') && onboardingStatus === 'pending_documents') {
    if (location.pathname !== '/onboarding/verify') {
      return <Navigate to="/onboarding/verify" replace />;
    }
  }

  // 5. Handle PENDING users (legacy - for backward compatibility)
  if (verificationStatus === 'PENDING') {
    // If they are not on the waiting room page, force them there.
    if (location.pathname !== '/onboarding/pending') {
      return <Navigate to="/onboarding/pending" replace />;
    }
  }

  // 6. Handle VERIFIED users.
  if (verificationStatus === 'VERIFIED' || onboardingStatus === 'verified') {
    // If a fully verified user tries to access any onboarding page,
    // redirect them away to their main dashboard.
    if (isOnboardingRoute) {
      return <Navigate to="/dashboard" replace />;
    }
  }
  
  // If none of the above conditions are met, the user is authorized to see the page.
  return <Outlet />;
};

export default ProtectedRoute;